import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import sizeMe, { SizeMeProps } from 'react-sizeme';

import { Dispatch } from 'redux';

import {
  subscribeToVideo,
  subscribeToAllVideos,
  toggleAllVideoMute,
  toggleVideoMute,
  changeVideoQuality,
} from '../../lib/actions/room';
import { VideoRoom } from '../../lib/api/videoroom';
import { getLogger } from '../../lib/logger';
import { State } from '../../lib/reducers';
import { WebSocketReady } from '../../lib/reducers/websocket';
import { VideoQualityOptions, StreamQualityValue } from '../../lib/redux_types';
import { amModerator, getModerators } from '../../lib/reduxSelectors/room';
import { isRecorder } from '../../lib/reduxSelectors/session';
import { getWsUserId } from '../../lib/reduxSelectors/websocket';
import { usePrevious } from '../../lib/utils/hooks';
import LoadingVideoElement from '../VideoElement/LoadingVideoElement';

import Fullscreen from './Fullscreen';
import GridUser from './GridUser';
import GridView from './GridView';
import useSubscriptionsWatcher from './hooks/useSubscriptionsWatcher';
import { getStreams } from './StandardDimensionLayout';
import usePublishersWatcher from './usePublishersWatcher';
import VideoToolbar from './VideoToolbar';


function getOrderedStreamKeys(remoteVideoStreams: ExtendedProps['remoteVideoStreams']) {
  const getDisplayName = (uid: string) => {
    const stream = remoteVideoStreams[uid as keyof ExtendedProps['remoteVideoStreams']];
    return (stream || { displayName: '' }).displayName;
  };

  const keysAndNames = Object.keys(remoteVideoStreams).map(
    (uid) => [ uid, getDisplayName(uid) ]
  );
  keysAndNames.sort((a, b) => {
    const name1 = a[1].toLowerCase();
    const name2 = b[1].toLowerCase();
    if (name1 > name2) return 1;
    else if (name1 < name2) return -1;
    else {
      if (a[0].endsWith('_screen')) {
        return -1;
      }
      else if (b[0].endsWith('_screen')) {
        return 1;
      }
      else {
        return 0;
      }
    }
  });
  return keysAndNames.map(([a, _b]) => a);
}


function renderParticipants(
  props: ExtendedProps,
  participantsGrid: ReturnType<typeof getGridStyles>,
  observedScreens: Set<string>,
  previousObservedScreens: Set<string>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>, // FIXME
) {
  const { remoteVideoStreams, hasVideoStream, localVideoStream, myUserId, localScreenStream } = props;
  const keys = getOrderedStreamKeys(remoteVideoStreams);
  const participants: React.ReactNodeArray = [];
  let idx = 0;

  if (hasVideoStream && myUserId) {
    participants.push(
      <div key={idx} style={participantsGrid[idx]}>
        <Fullscreen user={myUserId}>
          <LoadingVideoElement
            user={myUserId}
            mirrored={true}
            addVideoMutedIconOverlay={true}
            stream={localVideoStream}
          />
          <VideoToolbar uid={myUserId} />
        </Fullscreen>
      </div>
    );
    idx += 1;
  }

  if (localScreenStream && myUserId) {
    participants.push(
      <div key={idx} style={participantsGrid[idx]}>
        <Fullscreen user={`${myUserId}_screen`}>
          <LoadingVideoElement
            user={`${myUserId}_screen`}
            mirrored={false}
            addVideoMutedIconOverlay={true}
            stream={localScreenStream}
          />
          <VideoToolbar uid={`${myUserId}_screen`} />
        </Fullscreen>
      </div>
    );
    idx += 1;
  }

  // utility fun to check if an observed stream was previously observed or not
  // N.B. this does not check if it is currently observed, so you should only
  // pass arguments you're sure they are being currently watched
  const isNewlyObservedStream = (uid: string, userId: string) => {
    if (uid.endsWith('_screen') && !previousObservedScreens.has(userId)) {
      // newly observed screen
      return true;
    }
    if (!uid.endsWith('_screen') && previousObservedScreens.has(userId)) {
      // newly observed stream
      return true;
    }
    return false;
  };

  for (let i = 0; i < keys.length; i++) {
    const uid = keys[i];
    const video = remoteVideoStreams[uid as keyof ExtendedProps['remoteVideoStreams']];
    const userId = uid.replace(/_screen$/, '');
    if (uid.endsWith('_screen') && !observedScreens.has(userId)) {
      const participantStream = (
        remoteVideoStreams[userId as keyof ExtendedProps['remoteVideoStreams']] || { stream: null }
      )['stream'];
      // if the participant has a stream and we are not watching this screen,
      // just continue, their stream will be rendered
      // if not, render the screen anyway, since no other stream is available
      if (participantStream) {
        dispatch(toggleVideoMute(uid, true));
        continue;
      }
    }
    if (!uid.endsWith('_screen') && observedScreens.has(userId)) {
      // we are watching user's screen, mute it and avoid rendering it
      dispatch(toggleVideoMute(uid, true));
      continue;
    }
    if (video['stream'] || video['isVideoStarting']) {
      if (isNewlyObservedStream(uid, userId)) {
        // only unmute a stream if it wasn't already observed, to avoid
        // unmuting it because of a render and the user manually muted it
        dispatch(toggleVideoMute(uid, false));
      }
      participants.push(
        <GridUser key={idx} uid={uid} style={participantsGrid[idx]} stream={video['stream']} />
      );
      idx += 1;
    }
    else {
      dispatch(toggleVideoMute(uid, true));
      continue;
    }
  }
  return participants;
}


function getGridStyles(viewSize: SizeMeProps['size'], num: number) {
  const videoAspectRatio = 16 / 9;
  if (viewSize.width && viewSize.width > 0 && viewSize.height && viewSize.height > 0) {
    let gridSize = Math.ceil(Math.sqrt(num));
    gridSize = gridSize < 3 ? 3 : gridSize;

    let videoWidth = viewSize.width / gridSize;
    let videoHeight = videoWidth / videoAspectRatio;

    if (videoHeight * gridSize > viewSize.height) {
      // height is not enough to conatain all videos, constraint grid size by
      // height
      videoHeight = viewSize.height / gridSize;
      videoWidth = videoHeight * videoAspectRatio;
    }
    const res = [];

    // center grid on view
    const wOffset = Math.abs(gridSize * videoWidth - viewSize.width) / 2;
    const hOffset = Math.abs(gridSize * videoHeight - viewSize.height) / 2;

    let currentRow = 0;
    let currentCol = 0;
    for (let i = 1; i <= num; i++) {
      const el = {
        left: currentRow * videoWidth + wOffset,
        top: currentCol * videoHeight + hOffset,
        width: videoWidth,
        height: videoHeight,
        position: 'absolute' as React.CSSProperties["position"],
      };
      currentCol = currentRow + 1 < gridSize ? currentCol : (currentCol + 1) % gridSize;
      currentRow = (currentRow + 1) % gridSize;
      res.push(el);
    }
    return res;
  }
  else {
    return [];
  }
}


function calculateRemoteStreamsNumber(
  remoteVideoStreams: ExtendedProps['remoteVideoStreams'],
  observedScreens: Set<string>,
) {
  return Object.keys(remoteVideoStreams).reduce(
    (count, uid) => {
      const userId = uid.replace(/_screen$/, '');
      if (uid.endsWith('_screen') && !observedScreens.has(userId)) {
        const participantStream = (
          remoteVideoStreams[userId as keyof ExtendedProps['remoteVideoStreams']] || { stream: null }
        )['stream'];
        // count participant screen anyway if they do not have a valid stream
        if (participantStream) {
          return count;
        }
        else {
          return count + 1;
        }
      }
      if (!uid.endsWith('_screen') && observedScreens.has(userId)) {
        // this is a stream but we are observing his screen
        return count;
      }
      return count + 1;
    }, 0
  );
}


function ModeratorView(props: ExtendedProps & SizeMeProps) {
  const {
    remoteVideoStreams,
    size,
    layoutConfig,
  } = props;

  const dispatch = useDispatch();

  const observedScreens = layoutConfig.observedScreens || new Set();
  const previousObservedScreens = usePrevious(observedScreens, new Set());

  const newSubscriptions = useSubscriptionsWatcher();
  newSubscriptions.forEach((s) => {
    if ((s.kind === 'screen') && observedScreens.has(s.uid)) {
      dispatch(toggleVideoMute(`${s.uid}_screen`, false));
    }
    else if (s.kind === 'stream' && !observedScreens.has(s.uid)) {
      dispatch(toggleVideoMute(s.uid, false));
    }
  });

  if (size.width && size.width > 0 && size.height && size.height > 0) {
    let participantsCount = calculateRemoteStreamsNumber(remoteVideoStreams, observedScreens);
    if (props.hasVideoStream) participantsCount += 1;
    if (props.localScreenStream) participantsCount += 1;
    const participantsGrid = getGridStyles(size, participantsCount);
    return (
      <div style={{ width: '100%', height: '100%' }}>
        {renderParticipants(props, participantsGrid, observedScreens, previousObservedScreens, dispatch)}
      </div>
    );
  }
  else {
    return null;
  }
}


const SizedModeratorView = sizeMe({ monitorHeight: true })(ModeratorView);


function UserView(props: ExtendedProps & SizeMeProps & { moderators: string[] }) {
  const dispatch = useDispatch();

  const { moderators, ...restOfProps } = props;

  React.useEffect(
    () => {
      moderators.forEach((uid) => {
        dispatch(subscribeToVideo(uid));
        dispatch(subscribeToVideo(`${uid}_screen`));
      });

      const allButModerators = (uid: string) => !moderators.includes(uid);
      dispatch(toggleAllVideoMute(true, allButModerators));
      const allButUsers = (uid: string) => moderators.includes(uid);
      dispatch(toggleAllVideoMute(false, allButUsers));
    }, [dispatch, moderators]
  );

  const newSubscriptions = useSubscriptionsWatcher();
  newSubscriptions.forEach((s) => {
    const isModeratorStream = moderators.includes(s.uid);
    if ((s.kind === 'screen')) {
      dispatch(toggleVideoMute(`${s.uid}_screen`, !isModeratorStream));
    }
    else if (s.kind === 'stream') {
      dispatch(toggleVideoMute(s.uid, !isModeratorStream));
    }
  });

  // filter out non moderators' streams
  const remoteStreams = Object.fromEntries(Object.entries(restOfProps.remoteVideoStreams)
    .filter((el) => moderators.includes(el[0].replace(/_screen$/, ''))));

  return (
    <GridView { ...{ remoteVideoStreams: remoteStreams } } />
  );
}


const SizedUserView  = sizeMe({ monitorHeight: true })(UserView);


function shouldUseModeratorView(props: ExtendedProps) {
  const { amIRoomModerator, isRecorder } = props;

  return amIRoomModerator || isRecorder;
}


function LessonLayout(props: ExtendedProps) {
  const { amIRoomModerator, roomModerators } = props;

  const wasIRoomModerator = usePrevious(amIRoomModerator, amIRoomModerator);

  const dispatch = useDispatch();

  const useModeratorView = shouldUseModeratorView(props);

  if ((amIRoomModerator && !wasIRoomModerator) || props.isRecorder) {
    dispatch(subscribeToAllVideos());
  }

  const newPublishers = usePublishersWatcher();
  newPublishers.forEach((u) => {
    if (useModeratorView) {
      // if room moderator, automatically subscribe to everyone
      if (u.stream) {
        dispatch(subscribeToVideo(u.uid));
      }
      if (u.screen) {
        dispatch(subscribeToVideo(`${u.uid}_screen`));
      }
    }
    else {
      // if room user, only subscribe to moderators streams
      if (roomModerators.includes(u.uid)) {
        if (u.stream) {
          dispatch(subscribeToVideo(u.uid));
        }
        if (u.screen) {
          dispatch(subscribeToVideo(`${u.uid}_screen`));
        }
      }
    }
  });

  // no need to avoid useless calls since this api won't apply settings if no
  // change is needed
  if (useModeratorView) {
    const constraints: VideoQualityOptions = {
      streamQuality: props.configuredVideoQuality,
    };
    if (props.roomOptions.frame_rate) {
      constraints.frameRate = props.roomOptions.frame_rate;
    }
    dispatch(changeVideoQuality(constraints, getLogger('LessonLayout')));
  }
  else {
    const constraints: VideoQualityOptions = {
      streamQuality: StreamQualityValue.P180,
      frameRate: 5
    };
    dispatch(changeVideoQuality(constraints, getLogger('LessonLayout')));
  }

  if (useModeratorView) {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <SizedModeratorView {...props} />
      </div>
    );
  }
  else {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <SizedUserView {...props} moderators={roomModerators} />
      </div>
    );
  }
}


type MappedProps = {
  amIRoomModerator: boolean;
  isRecorder: boolean;
  hasVideoStream: boolean;
  localVideoStream: State['room']['localvideo_stream'];
  localScreenStream: State['room']['screenStream'];
  remoteVideoStreams: ReturnType<typeof getStreams>;
  myUserId: WebSocketReady['uid'];
  roomModerators: string[];
  layout: State['room']['layout'];
  layoutConfig: State['room']['layoutConfig'];
  configuredVideoQuality: undefined | VideoQualityOptions['streamQuality'];
  roomOptions: State['appconfig']['room_options'];
}


type Props = {} & RouteComponentProps<{ id: string }>

type ExtendedProps = Props & MappedProps;


const mapStateToProps = (state: State, { match }: Props): MappedProps => {
  let hasVideoStream = false;
  if (state.room && state.room.localvideo_stream) {
    hasVideoStream = Boolean(VideoRoom.getVideoTrackFromStream(state.room.localvideo_stream));
  }

  const recorder = isRecorder(state, match.url);
  return {
    isRecorder: recorder,
    amIRoomModerator: amModerator(state),
    myUserId: getWsUserId(state.websocket),
    localVideoStream: state.room.localvideo_stream,
    localScreenStream: state.room.screenStream,
    remoteVideoStreams: getStreams(state),
    hasVideoStream,
    roomModerators: getModerators(state),
    layout: state.room.layout,
    layoutConfig: state.room.layoutConfig,
    configuredVideoQuality: state.settings.videoQuality ? state.settings.videoQuality.value : undefined,
    roomOptions: state.appconfig.room_options,
  };
};

export default withRouter(connect(mapStateToProps)(LessonLayout));
